import React, { useState, useEffect } from 'react';
import { Save, Eye, Copy, Code, Loader, CheckCircle, XCircle, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

interface FieldConfig {
  enabled: boolean;
  required: boolean;
  label: string;
  fieldType?: 'text' | 'textarea' | 'email' | 'tel' | 'url';
}

interface LeadFormSettings {
  _id?: string;
  accountId: string;
  isEnabled: boolean;
  style: {
    theme: 'light' | 'dark' | 'modern' | 'minimal' | 'professional';
    primaryColor: string;
    backgroundColor?: string;
    borderRadius: number;
    fontFamily: string;
    buttonText: string;
    submitButtonColor: string;
  };
  fields: {
    [key: string]: FieldConfig;
  };
  successMessage: string;
  redirectUrl?: string;
}

// Available Client fields that can be added to the form
const AVAILABLE_CLIENT_FIELDS = [
  { key: 'addressStreet', label: 'Street Address', fieldType: 'text' as const },
  { key: 'addressCity', label: 'City', fieldType: 'text' as const },
  { key: 'addressState', label: 'State/Province', fieldType: 'text' as const },
  { key: 'addressPostalCode', label: 'Postal Code', fieldType: 'text' as const },
  { key: 'addressCountry', label: 'Country', fieldType: 'text' as const },
  { key: 'taxNumber', label: 'Tax Number', fieldType: 'text' as const }
];

export const LeadFormSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [accountId, setAccountId] = useState<string>('');

  const [settings, setSettings] = useState<LeadFormSettings>({
    accountId: '',
    isEnabled: false,
    style: {
      theme: 'modern',
      primaryColor: '#3B82F6',
      backgroundColor: '#ffffff',
      borderRadius: 8,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      buttonText: 'Submit Inquiry',
      submitButtonColor: '#3B82F6'
    },
    fields: {
      name: { enabled: true, required: true, label: 'Name', fieldType: 'text' },
      company: { enabled: true, required: false, label: 'Company', fieldType: 'text' },
      email: { enabled: true, required: true, label: 'Email', fieldType: 'email' },
      phone: { enabled: true, required: false, label: 'Phone', fieldType: 'tel' },
      description: { enabled: true, required: true, label: 'Description', fieldType: 'textarea' }
    },
    successMessage: 'Thank you for your inquiry! We will get back to you soon.'
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchSettings();
    if (user?.currentAccountId) {
      setAccountId(user.currentAccountId);
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/lead-form-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSettings(data.data);
        if (data.data.accountId) {
          setAccountId(data.data.accountId);
        }
      } else {
        setError(data.message || 'Failed to fetch settings');
      }
    } catch (err) {
      console.error('Fetch settings error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/lead-form-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(t('admin.leadFormSettings.saved'));
        setSettings(data.data);
      } else {
        setError(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Save settings error:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getEmbedScript = () => {
    if (!accountId) return '';
    const baseUrl = apiUrl.replace('/api', '');
    return `<script src="${baseUrl}/lead-form.js" data-account-id="${accountId}"></script>`;
  };

  const copyScript = () => {
    const script = getEmbedScript();
    navigator.clipboard.writeText(script);
    setSuccess(t('admin.leadFormSettings.scriptCopied'));
    setTimeout(() => setSuccess(null), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-accent-600" />
      </div>
    );
  }

  const renderField = (fieldKey: string, field: FieldConfig) => {
    const style = settings.style;
    const isDark = style.theme === 'dark';
    const fieldType = field.fieldType || 'text';
    const inputBackground = isDark ? '#111827' : '#ffffff';
    const inputBorder = isDark ? '#374151' : '#d1d5db';
    const textColor = isDark ? '#f9fafb' : '#111827';
    const labelColor = isDark ? '#f9fafb' : '#374151';

    if (fieldType === 'textarea') {
      return (
        <div key={fieldKey} style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            color: labelColor,
            fontSize: '14px'
          }}>
            {field.label}{field.required && ' *'}
          </label>
          <textarea
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${inputBorder}`,
              borderRadius: `${style.borderRadius}px`,
              fontFamily: style.fontFamily,
              background: inputBackground,
              color: textColor,
              fontSize: '14px',
              boxSizing: 'border-box',
              resize: 'vertical'
            }}
            placeholder={field.label}
          />
        </div>
      );
    }

    return (
      <div key={fieldKey} style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 500,
          color: labelColor,
          fontSize: '14px'
        }}>
          {field.label}{field.required && ' *'}
        </label>
        <input
          type={fieldType}
          style={{
            width: '100%',
            padding: '12px',
            border: `1px solid ${inputBorder}`,
            borderRadius: `${style.borderRadius}px`,
            fontFamily: style.fontFamily,
            background: inputBackground,
            color: textColor,
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
          placeholder={field.label}
        />
      </div>
    );
  };

  const renderPreview = () => {
    const style = settings.style;
    const fields = settings.fields;
    const backgroundColor = style.backgroundColor || (style.theme === 'dark' ? '#1f2937' : '#ffffff');
    
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div 
          id="preview-form-wrapper"
          style={{
            background: backgroundColor,
            borderRadius: `${style.borderRadius}px`,
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <form id="preview-form">
            {Object.entries(fields).map(([key, field]) => 
              field.enabled && renderField(key, field)
            )}
            <button
              type="button"
              style={{
                width: '100%',
                padding: '12px 24px',
                background: style.submitButtonColor,
                color: 'white',
                border: 'none',
                borderRadius: `${style.borderRadius}px`,
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: style.fontFamily
              }}
            >
              {style.buttonText}
            </button>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-1">
            <button
              type="button"
              onClick={() => setSettings({ ...settings, isEnabled: true })}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                settings.isEnabled
                  ? 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.leadFormSettings.enabled')}
            </button>
            <button
              type="button"
              onClick={() => setSettings({ ...settings, isEnabled: false })}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                !settings.isEnabled
                  ? 'bg-gray-500 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {t('admin.leadFormSettings.disabled')}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? t('admin.leadFormSettings.hidePreview') : t('admin.leadFormSettings.showPreview')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('common.save')}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-800 dark:text-red-200">
          <XCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-green-800 dark:text-green-200">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Preview Section */}
      {showPreview && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('admin.leadFormSettings.preview')}
          </h3>
          {renderPreview()}
        </div>
      )}

      {/* Embed Script Section */}
      {settings.isEnabled && accountId && (
        <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.leadFormSettings.embedScript')}
            </h3>
            <button
              onClick={copyScript}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <Copy className="w-4 h-4" />
              {t('common.copy')}
            </button>
          </div>
          <div className="bg-white dark:bg-gray-900 p-4 rounded border border-gray-200 dark:border-gray-700">
            <code className="text-sm text-gray-800 dark:text-gray-200 break-all">
              {getEmbedScript()}
            </code>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            {t('admin.leadFormSettings.embedScriptDescription')}
          </p>
        </div>
      )}

      {/* Style Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('admin.leadFormSettings.styleSettings')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.leadFormSettings.theme')}
            </label>
            <select
              value={settings.style.theme}
              onChange={(e) => setSettings({
                ...settings,
                style: { ...settings.style, theme: e.target.value as any }
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="light">{t('admin.leadFormSettings.themeLight')}</option>
              <option value="dark">{t('admin.leadFormSettings.themeDark')}</option>
              <option value="modern">{t('admin.leadFormSettings.themeModern')}</option>
              <option value="minimal">{t('admin.leadFormSettings.themeMinimal')}</option>
              <option value="professional">{t('admin.leadFormSettings.themeProfessional')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.leadFormSettings.primaryColor')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={settings.style.primaryColor}
                onChange={(e) => setSettings({
                  ...settings,
                  style: { ...settings.style, primaryColor: e.target.value }
                })}
                className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={settings.style.primaryColor}
                onChange={(e) => setSettings({
                  ...settings,
                  style: { ...settings.style, primaryColor: e.target.value }
                })}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.leadFormSettings.borderRadius')} ({settings.style.borderRadius}px)
            </label>
            <input
              type="range"
              min="0"
              max="20"
              value={settings.style.borderRadius}
              onChange={(e) => setSettings({
                ...settings,
                style: { ...settings.style, borderRadius: parseInt(e.target.value) }
              })}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.leadFormSettings.buttonText')}
            </label>
            <input
              type="text"
              value={settings.style.buttonText}
              onChange={(e) => setSettings({
                ...settings,
                style: { ...settings.style, buttonText: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.leadFormSettings.submitButtonColor')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={settings.style.submitButtonColor}
                onChange={(e) => setSettings({
                  ...settings,
                  style: { ...settings.style, submitButtonColor: e.target.value }
                })}
                className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={settings.style.submitButtonColor}
                onChange={(e) => setSettings({
                  ...settings,
                  style: { ...settings.style, submitButtonColor: e.target.value }
                })}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.leadFormSettings.backgroundColor')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={settings.style.backgroundColor || '#ffffff'}
                onChange={(e) => setSettings({
                  ...settings,
                  style: { ...settings.style, backgroundColor: e.target.value }
                })}
                className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={settings.style.backgroundColor || '#ffffff'}
                onChange={(e) => setSettings({
                  ...settings,
                  style: { ...settings.style, backgroundColor: e.target.value }
                })}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Field Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.leadFormSettings.fieldSettings')}
          </h3>
          <div className="flex items-center gap-2">
            <select
              value=""
              onChange={(e) => {
                const selectedField = AVAILABLE_CLIENT_FIELDS.find(f => f.key === e.target.value);
                if (selectedField && !settings.fields[selectedField.key]) {
                  setSettings({
                    ...settings,
                    fields: {
                      ...settings.fields,
                      [selectedField.key]: {
                        enabled: true,
                        required: false,
                        label: selectedField.label,
                        fieldType: selectedField.fieldType
                      }
                    }
                  });
                }
                e.target.value = '';
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">{t('admin.leadFormSettings.addField')}</option>
              {AVAILABLE_CLIENT_FIELDS
                .filter(field => !settings.fields[field.key])
                .map(field => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="space-y-4">
          {Object.entries(settings.fields).map(([key, field]) => {
            const isStandardField = ['name', 'company', 'email', 'phone', 'description'].includes(key);
            return (
              <div key={key} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={field.enabled}
                      onChange={(e) => setSettings({
                        ...settings,
                        fields: {
                          ...settings.fields,
                          [key]: { ...field, enabled: e.target.checked }
                        }
                      })}
                      className="w-4 h-4 text-accent-600 rounded focus:ring-blue-500"
                    />
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {field.label || key}
                    </span>
                  </div>
                  {field.enabled && (
                    <div className="ml-7 space-y-2">
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => setSettings({
                          ...settings,
                          fields: {
                            ...settings.fields,
                            [key]: { ...field, label: e.target.value }
                          }
                        })}
                        placeholder={t('admin.leadFormSettings.fieldLabel')}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => setSettings({
                              ...settings,
                              fields: {
                                ...settings.fields,
                                [key]: { ...field, required: e.target.checked }
                              }
                            })}
                            className="w-4 h-4 text-accent-600 rounded focus:ring-blue-500"
                          />
                          {t('admin.leadFormSettings.required')}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                {!isStandardField && (
                  <button
                    type="button"
                    onClick={() => {
                      const newFields = { ...settings.fields };
                      delete newFields[key];
                      setSettings({
                        ...settings,
                        fields: newFields
                      });
                    }}
                    className="p-2 text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
                    title={t('admin.leadFormSettings.removeField')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Success Message */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('admin.leadFormSettings.successMessage')}
        </h3>
        <textarea
          value={settings.successMessage}
          onChange={(e) => setSettings({ ...settings, successMessage: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          placeholder={t('admin.leadFormSettings.successMessagePlaceholder')}
        />
      </div>

      {/* Redirect URL */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('admin.leadFormSettings.redirectUrl')}
        </h3>
        <input
          type="url"
          value={settings.redirectUrl || ''}
          onChange={(e) => setSettings({ ...settings, redirectUrl: e.target.value })}
          placeholder="https://example.com/thank-you"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          {t('admin.leadFormSettings.redirectUrlDescription')}
        </p>
      </div>
    </div>
  );
};

